// ===========================================
// BUSTED! BLACKJACK - LOGIN PAGE
// ===========================================

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { api } from '../api';
import { APP_VERSION } from '../version';
import bustedLogo from '../assets/busted.png';

// ===========================================
// COMING SOON MODAL
// ===========================================

interface ComingSoonModalProps {
  isOpen: boolean;
  provider: string;
  onClose: () => void;
}

function ComingSoonModal({ isOpen, provider, onClose }: ComingSoonModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-gray-800 rounded-xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl border border-gray-700">
        <h2 className="text-3xl font-bold text-casino-gold-400 mb-4">
          COMING SOON!
        </h2>
        <p className="text-gray-300 mb-6">
          {provider} login is not yet available.<br />
          Please use another sign-in method.
        </p>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-casino-gold-500 hover:bg-casino-gold-400 text-black font-bold rounded-lg transition-colors"
        >
          OK
        </button>
      </div>
    </div>
  );
}

// ===========================================
// LOGIN PAGE
// ===========================================

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuthStore();

  // Coming Soon modal state
  const [comingSoonModal, setComingSoonModal] = useState<{ isOpen: boolean; provider: string }>({
    isOpen: false,
    provider: '',
  });

  const error = searchParams.get('error');

  // Get the redirect path from location state (set by ProtectedRoute)
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/home';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  const handleGoogleLogin = () => {
    window.location.href = api.auth.getGoogleLoginUrl(from);
  };

  const handleDiscordLogin = () => {
    window.location.href = api.auth.getDiscordLoginUrl(from);
  };

  const handleTwitterLogin = () => {
    window.location.href = api.auth.getTwitterLoginUrl(from);
  };

  // Facebook login - COMING SOON (underlying code preserved for later)
  const handleFacebookLogin = () => {
    // TODO: Enable when Facebook OAuth is configured
    // window.location.href = api.auth.getFacebookLoginUrl(from);
    setComingSoonModal({ isOpen: true, provider: 'Facebook' });
  };

  const _handleAppleLogin = () => {
    window.location.href = api.auth.getAppleLoginUrl(from);
  };

  const _handleWeChatLogin = () => {
    window.location.href = api.auth.getWeChatLoginUrl(from);
  };

  const _handleMicrosoftLogin = () => {
    window.location.href = api.auth.getMicrosoftLoginUrl(from);
  };

  // TikTok login - COMING SOON (underlying code preserved for later)
  const handleTikTokLogin = () => {
    // TODO: Enable when TikTok OAuth is configured
    // window.location.href = api.auth.getTikTokLoginUrl(from);
    setComingSoonModal({ isOpen: true, provider: 'TikTok' });
  };

  const getErrorMessage = (errorCode: string | null): string | null => {
    switch (errorCode) {
      case 'oauth_denied':
        return 'You cancelled the login process.';
      case 'oauth_failed':
        return 'Login failed. Please try again.';
      case 'invalid_state':
      case 'expired_state':
        return 'Session expired. Please try again.';
      case 'no_code':
        return 'Authentication error. Please try again.';
      default:
        // Don't show generic error for unknown codes
        return null;
    }
  };

  const errorMessage = getErrorMessage(error);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-4">
          <img src={bustedLogo} alt="BUSTED!" className="h-24 mx-auto" />
          <h1 className="text-3xl font-bold text-white">Busted Blackjack</h1>
          <p className="text-gray-400">Multiplayer Social Games</p>
        </div>

        {/* Login Card */}
        <div className="card">
          <div className="card-body space-y-4">
            <h2 className="text-xl font-semibold text-center text-white">Sign In</h2>

            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm text-center">
                {errorMessage}
              </div>
            )}

            {/* OAuth Buttons - Sorted by global users (descending) */}
            <div className="space-y-3">
              {/* Facebook - 3B+ users */}
              <button
                onClick={handleFacebookLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#1877F2] text-white rounded-lg font-medium hover:bg-[#166FE5] transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Continue with Facebook
              </button>

              {/* Google - 1.8B+ users */}
              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-gray-800 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* Apple - 1.5B+ users - COMMENTED OUT FOR LATER
              <button
                onClick={handleAppleLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-900 transition-colors border border-gray-700"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
              </button>
              */}

              {/* TikTok - 1.5B+ users */}
              <button
                onClick={handleTikTokLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-900 transition-colors border border-gray-700"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                Continue with TikTok
              </button>

              {/* Microsoft - 1.4B+ users - COMMENTED OUT FOR LATER
              <button
                onClick={handleMicrosoftLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#2F2F2F] text-white rounded-lg font-medium hover:bg-[#404040] transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M0 0h11.377v11.372H0zm12.623 0H24v11.372H12.623zM0 12.623h11.377V24H0zm12.623 0H24V24H12.623z"/>
                </svg>
                Continue with Microsoft
              </button>
              */}

              {/* WeChat - 1.3B+ users - COMMENTED OUT FOR LATER
              <button
                onClick={handleWeChatLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#07C160] text-white rounded-lg font-medium hover:bg-[#06AD56] transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.045c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088v-.002c-.135-.01-.27-.023-.406-.032zm-2.084 3.222c.536 0 .97.44.97.982a.976.976 0 0 1-.97.983.976.976 0 0 1-.97-.983c0-.542.434-.982.97-.982zm4.857 0c.536 0 .97.44.97.982a.976.976 0 0 1-.97.983.976.976 0 0 1-.97-.983c0-.542.434-.982.97-.982z"/>
                </svg>
                Continue with WeChat
              </button>
              */}

              {/* Twitter/X - 500M+ users */}
              <button
                onClick={handleTwitterLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Continue with X
              </button>

              {/* Discord - 200M+ users */}
              <button
                onClick={handleDiscordLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#5865F2] text-white rounded-lg font-medium hover:bg-[#4752C4] transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                Continue with Discord
              </button>
            </div>

            {/* Info Text */}
            <p className="text-center text-gray-500 text-sm">
              By signing in, you agree to our <a href="/terms.html" className="text-casino-gold-400 hover:underline">Terms of Service</a>
              <br />
              and confirm you are 18+ years old.
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center text-sm text-gray-500">
          <div>Play Money Only • Not Real Gambling • <a href="/privacy.html" className="text-gray-400 hover:text-casino-gold-400 hover:underline">Privacy Policy</a></div>
          <div>BUSTED! Blackjack <span className="text-xs text-gray-600">v{APP_VERSION}</span></div>
        </footer>
      </div>

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={comingSoonModal.isOpen}
        provider={comingSoonModal.provider}
        onClose={() => setComingSoonModal({ isOpen: false, provider: '' })}
      />
    </div>
  );
}

export default LoginPage;
